<template>
  <div class="files">
    <div v-if="title" class="files__header">
      <h3 class="files__header-title">{{ title }}</h3>
      <div class="files__header-tooltip">
        <span v-if="tooltip" class="files__header-tooltip-icon material-icons">
          info
        </span>
        <span class="files__header-tooltip-text">
          The following files are available in your current path: for example,
          the following code is valid <code>open('movies.csv')</code>
        </span>
      </div>
    </div>
    <div v-for="file in files" :key="file.id" class="file">
      <Accordion
        :arrow="false"
        :open="false"
        :canOpen="canOpen"
        :transparent="transparent"
        @open="render(file)"
      >
        <template #header>
          <div class="file__header">
            <DownloadIconCourse
              v-if="hideDownloadIcon"
              class="file__header-course-icon"
            />
            <div class="file__header-name" @click.stop="download(file)">
              {{ file.name }}
            </div>
            <div class="file__header-status-wrapper">
              <DownloadIcon
                v-if="!loadingDownload[file.id] && !hideDownloadIcon"
                class="file__header-download-icon"
                @click.stop="download(file)"
              />
              <ActionLoader
                v-if="loadingDownload[file.id]"
                loaderClass="file__loading-spinner"
                name="ball-clip-rotate"
              />
            </div>
          </div>
        </template>
        <template #body>
          <div v-if="file.has_renderer" class="file__body">
            <ActionLoader
              v-if="loadingRender && !renderedFiles[file.id]"
              loaderClass="file__loading-spinner"
              name="ball-clip-rotate"
            />
            <div v-if="renderedFiles[file.id]">
              <div
                v-if="file.type !== 'json' && !prismFiles.includes(file.type)"
                class="file__body-render-box"
                v-html="renderedFiles[file.id]"
              ></div>
              <json-viewer
                v-if="file.type === 'json'"
                :value="JSON.parse(renderedFiles[file.id])"
                :expand-depth="2"
                copyable
              ></json-viewer>
              <div
                v-if="prismFiles.includes(file.type)"
                ref="filerendered"
                class="file__body-render-box"
              >
                <prism :language="file.type">{{
                  renderedFiles[file.id]
                }}</prism>
              </div>
            </div>
          </div>
          <div v-else>This file has no preview.</div>
        </template>
      </Accordion>
    </div>
    <slot v-if="!loading && filesUUIDs && !filesUUIDs.length" name="no-files" />
    <ActionLoader
      v-if="loading"
      loaderClass="file__loading-spinner"
      name="ball-clip-rotate"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Prism from 'vue-prism-component'
import JsonViewer from 'vue-json-viewer'
import DownloadIcon from '@/assets/icons/course/download-icon.svg'
import DownloadIconCourse from '@/assets/icons/course/download.svg'
import ActionLoader from '@/components/shared/ActionLoader.vue'
import Accordion from './Accordion.vue'
import 'prismjs/components/prism-sql'

export default {
  name: 'Files',
  components: {
    Accordion,
    DownloadIcon: { ...DownloadIcon, name: 'DownloadIcon' },
    DownloadIconCourse: { ...DownloadIconCourse, name: 'DownloadIconCourse' },
    JsonViewer,
    Prism,
    ActionLoader,
  },
  props: {
    content: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      required: false,
    },
    canOpen: {
      type: Boolean,
      required: false,
      default: () => {
        return true
      },
    },
    transparent: {
      type: Boolean,
      required: false,
    },
    tooltip: {
      type: Boolean,
      required: false,
      default: () => {
        return false
      },
    },
    hideDownloadIcon: {
      type: Boolean,
      required: false,
      default: () => {
        return false
      },
    },
    contentType: {
      type: String,
      required: false,
    },
  },
  data: () => {
    return {
      localFiles: [],
      loading: false,
      loadingRender: false,
      loadingDownload: {},
      prismFiles: ['sql', 'python', 'py'],
    }
  },
  computed: {
    ...mapGetters('files', {
      contentFiles: 'getCurrentFilesById',
      rendered: 'getCurrentFilesRendered',
    }),
    files() {
      return this.contentFiles(this.filesUUIDs)
    },
    filesUUIDs() {
      return this.contentType === 'course'
        ? this.content.files_uuids
        : this.content.files
    },
    renderedFiles() {
      return this.rendered(this.filesUUIDs)
    },
  },
  watch: {
    content() {
      this.getFiles()
    },
  },
  mounted() {
    this.getFiles()
  },
  methods: {
    async getFiles() {
      if (this.filesUUIDs && this.filesUUIDs.length) {
        this.loading = true
        this.localFiles = await this.$store.dispatch(
          'files/getFiles',
          this.filesUUIDs
        )
        this.loading = false
      }
    },
    async download(file) {
      this.$set(this.loadingDownload, file.id, true)
      const response = await this.$store.dispatch(
        'files/getFileDownloadLink',
        file.id
      )
      this.$delete(this.loadingDownload, file.id)
      const anchor = document.createElement('a')
      anchor.href = response.download_url
      anchor.download = response.filename
      anchor.click()
    },
    async render(file) {
      if (file.has_renderer && !this.renderedFiles[file.id]) {
        this.loadingRender = true
        await this.$store.dispatch('files/getFileRender', file.id)
        this.loadingRender = false
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.files {
  padding: 35px 35px;
  width: 100%;
  &__header {
    display: flex;
    align-content: center;
  }
  &__header-title {
    font-size: 20px;
    margin-top: 0px;
  }
  &__header-tooltip {
    $this: &;
    position: relative;
    display: flex;
    align-content: center;
    &-text {
      position: absolute;
      right: 0;
      width: 250px;
      display: none;
      transform: translateX(103%);
      font-size: 13px;
      font-weight: normal;
      background: black;
      padding: 12px 20px;
      color: #a9a9a9;
      border-radius: 5px;
      code {
        color: white;
        background: #303031;
        padding: 2px 5px;
        border-radius: 5px;
      }
      &:after {
        content: '';
        position: absolute;
        top: 30%;
        right: 100%;
        margin-top: -11px;
        border-width: 10px;
        border-style: solid;
        border-color: transparent #000 transparent transparent;
      }
    }
    &-icon {
      display: flex;
      margin-bottom: 18px;
      margin-left: 0.5em;
      color: #6e6e73;
    }
    &:hover #{$this}-text {
      display: block;
    }
  }
  &--courses {
    padding: 0;
    font-weight: bold;
    font-size: 14px;
    padding-top: 3px;
  }
}
.file {
  margin-bottom: 5px;
  color: white;

  &__header {
    display: flex;
    align-content: center;
    justify-content: space-between;
    align-items: center;
    margin: 0px;

    &-name {
      margin-left: 10px;
      cursor: pointer;
      word-break: break-all;
      flex-shrink: 50;
    }
    &-course-icon {
      width: 15px;
      height: 15px;
    }

    &-download-icon {
      cursor: pointer;
      color: var(--light-blue-gray);
      &:hover {
        color: var(--white);
      }
    }

    &-status {
      &-wrapper {
        margin-left: auto;
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 5px;
      }
    }
  }
  &__loading-spinner {
    margin-left: auto;
  }
}

.prism-code {
  background-color: #000000;
  font-size: 0.8em;
  margin-top: 15px;
  margin-bottom: 10px;
  padding: 20px 0px;
  border: 1px solid #424040;
}
</style>
